import { useMemo } from 'react';

import type { SliderMark } from '~components';
import { Slider } from '~components';
import { snapRangeToNearest } from '../utils';

/* -------------------------------------------------------------------------- */
/*                                    Types                                   */
/* -------------------------------------------------------------------------- */

type RLRangeSliderProps = {
  /** Minimum RL value */
  min: number;
  /** Maximum RL value */
  max: number;
  /** Current RL range [minRL, maxRL] */
  value: [number, number];
  /** Callback when range changes */
  onChange: (range: [number, number]) => void;
  /** Valid RL values to snap to (for snap-to-marks behavior) */
  validRLs?: number[];
  /** Show the current range values as labels on both sides. Defaults to true */
  showValueLabels?: boolean;
};

/* -------------------------------------------------------------------------- */
/*                               Main component                               */
/* -------------------------------------------------------------------------- */

/**
 * Feature-specific wrapper around the generic Slider for RL (Reduced Level) range filtering.
 * Displays current min/max RL values as labels at both ends.
 * Supports snap-to-marks behavior when validRLs are provided.
 *
 * @example
 * ```tsx
 * const validRLs = [...new Set(holes.map((hole) => hole.rl))].sort((a, b) => a - b);
 * const rlBounds = [validRLs[0], validRLs[validRLs.length - 1]];
 *
 * <RLRangeSlider
 *   min={rlBounds[0]}
 *   max={rlBounds[1]}
 *   value={rlRange ?? rlBounds}
 *   onChange={setRlRange}
 *   validRLs={validRLs}
 * />
 * ```
 */
const RLRangeSlider = ({ min, max, value, onChange, validRLs = [], showValueLabels = true }: RLRangeSliderProps) => {
  const handleChange = (newValue: number[]) => {
    if (newValue.length === 2) {
      const [min, max] = snapRangeToNearest([newValue[0], newValue[1]], validRLs);
      onChange([min, max]);
    }
  };

  const marks: SliderMark[] = useMemo(() => validRLs.map((rl) => ({ value: rl })), [validRLs]);

  return (
    <div className="flex items-center gap-2">
      {showValueLabels && <span className="text-muted-foreground text-xs">{value[0]}</span>}
      <Slider
        value={value}
        onValueChange={handleChange}
        min={min}
        max={max}
        step={1}
        aria-label="RL range"
        marks={marks}
      />
      {showValueLabels && <span className="text-muted-foreground text-xs">{value[1]}</span>}
    </div>
  );
};

/* -------------------------------------------------------------------------- */
/*                                   Exports                                  */
/* -------------------------------------------------------------------------- */

export { RLRangeSlider };
